/**
 * SkillSelector Component
 * 
 * A hierarchical dropdown component for selecting skills.
 * Shows the full skill path (e.g., "Manufacturing > Welding > MIG Welding")
 * Supports both single and multi-select modes.
 */

import React, { useState, useEffect } from 'react';
import {
  skillService,
  SkillParameter,
  SkillTreeNode
} from '../../services/skillService';
import styles from './SkillSelector.module.css';

interface SkillSelectorProps {
  value?: string | string[];  // Selected skill ID(s)
  onChange: (value: string | string[]) => void;
  multiSelect?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const SkillSelector: React.FC<SkillSelectorProps> = ({
  value,
  onChange,
  multiSelect = false,
  placeholder = 'Select skill...',
  disabled = false
}) => {
  const [skillTree, setSkillTree] = useState<SkillTreeNode[]>([]);
  const [flatSkills, setFlatSkills] = useState<SkillParameter[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [skillPaths, setSkillPaths] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    setLoading(true);
    try {
      const [tree, skills] = await Promise.all([
        skillService.getSkillTree(),
        skillService.getSkills()
      ]);
      
      const roots = tree?.roots || [];
      setSkillTree(roots);
      setFlatSkills(Array.isArray(skills) ? skills : []);
      
      // Build skill paths from tree structure (no API calls needed)
      const paths = buildPathsFromTree(roots);
      setSkillPaths(paths);
    } catch (err) {
      console.error('Error loading skills:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Build skill paths by traversing the tree structure.
   * For each node, builds the path from root to that node.
   */
  const buildPathsFromTree = (roots: SkillTreeNode[]): Map<string, string> => {
    const paths = new Map<string, string>();

    const traverse = (node: SkillTreeNode, currentPath: string[] = []) => {
      if (!node || !node.id) return;

      const path = [...currentPath, node.name || 'Unnamed Skill'];
      paths.set(node.id, path.join(' > '));

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: SkillTreeNode) => {
          traverse(child, path);
        });
      }
    };

    roots.forEach(root => traverse(root));
    return paths;
  };

  const handleSkillClick = (skillId: string) => {
    if (multiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(skillId)
        ? currentValues.filter(id => id !== skillId)
        : [...currentValues, skillId];
      onChange(newValues);
    } else {
      onChange(skillId);
      setIsOpen(false);
    }
  };

  const getSelectedLabels = (): string => {
    if (!value) return '';
    
    const selectedIds = Array.isArray(value) ? value : [value];
    const labels = selectedIds
      .map(id => skillPaths.get(id) || flatSkills.find(s => s.id === id)?.name || '')
      .filter(Boolean);
    
    return labels.join(', ');
  };

  const isSkillSelected = (skillId: string): boolean => {
    if (!value) return false;
    return Array.isArray(value) ? value.includes(skillId) : value === skillId;
  };

  const filterSkills = (node: SkillTreeNode): boolean => {
    if (!searchTerm) return true;
    
    const matchesSearch = node.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const childrenMatch = node.children && Array.isArray(node.children) 
      ? node.children.some((child: SkillTreeNode) => filterSkills(child))
      : false;
    
    return matchesSearch || childrenMatch;
  };

  const renderSkillNode = (node: SkillTreeNode, depth: number = 0): React.ReactNode => {
    if (!node || !node.id) return null;
    if (!filterSkills(node)) return null;

    const isSelected = isSkillSelected(node.id);
    const nodeName = node.name || 'Unnamed Skill';
    const path = skillPaths.get(node.id) || nodeName;

    return (
      <div key={node.id} className={styles.skillOptionGroup}>
        <div
          className={`${styles.skillOption} ${isSelected ? styles.selected : ''}`}
          style={{ paddingLeft: `${depth * 16}px` }}
          onClick={() => handleSkillClick(node.id)}
        >
          <span className={styles.skillOptionIndent} style={{ width: `${depth * 16}px` }} />
          <div className={styles.skillOptionContent}>
            <span className={styles.skillOptionName}>{nodeName}</span>
            {path !== nodeName && (
              <span className={styles.skillOptionPath}>{path}</span>
            )}
          </div>
          {multiSelect && isSelected && (
            <span className={styles.skillOptionCheck}>✓</span>
          )}
        </div>
        {node.children && Array.isArray(node.children) && node.children.length > 0 && (
          <div className={styles.skillOptionChildren}>
            {node.children.map((child: SkillTreeNode) => renderSkillNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.skillSelector} ${disabled ? styles.disabled : ''}`}>
      <div
        className={styles.skillSelectorInput}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={getSelectedLabels() ? '' : 'placeholder'}>
          {getSelectedLabels() || placeholder}
        </span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && !disabled && (
        <div className={styles.skillSelectorDropdown}>
          <div className={styles.skillSelectorSearch}>
            <input
              type="text"
              placeholder="Search skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Prevent Enter key from submitting parent form
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          </div>

          <div className={styles.skillSelectorOptions}>
            {loading ? (
              <div className={styles.skillSelectorLoading}>Loading skills...</div>
            ) : (
              <>
                {skillTree.map(node => renderSkillNode(node))}
                {skillTree.length === 0 && (
                  <div className={styles.skillSelectorEmpty}>No skills available</div>
                )}
              </>
            )}
          </div>

          {multiSelect && (
            <div className={styles.skillSelectorFooter}>
              <button
                className="btn-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
              >
                Clear All
              </button>
              <button
                className={styles.btnDone}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

